import { CheckIcon, MinusIcon } from '@chakra-ui/icons'
import {
  Badge,
  Button,
  Center,
  CircularProgress,
  Divider,
  Flex,
  HStack,
  Menu,
  MenuButton,
  MenuButtonProps,
  MenuItem,
  MenuList,
  Portal,
  Text,
  Tooltip,
  useDisclosure,
  VStack,
} from '@chakra-ui/react'
import { useApp } from '@hb/react'
import {
  AssessmentStage,
  assessmentStageKeys,
  colors,
  getDateString,
  PracticeAssessmentStage,
  practiceAssessmentStageKeys,
  StageHistoryItem,
} from '@hb/shared'
import { useMemo } from 'react'
import { useProfile } from '../../../../../contexts/user/ProfileContext'
import { ActionButton, SolidActionButton } from '../../../../Buttons'
import { PregnancyArchiveStatus } from './ArchiveButton'
import { usePregnancyStatus } from './hooks'
import { PregnancyStatusHistoryButton } from './StatusHistory'

const AssessmentStatusButton = ({
  mode,
  stages,
  onClick,
  id,
  hovered,
  selectedIds,
  onHover,
  onBlur,
  isLoading,
  readOnly,
}: {
  mode: 'add' | 'move' | null
  id: AssessmentStage | PracticeAssessmentStage
  stages?: Array<AssessmentStage | PracticeAssessmentStage>
  onClick: () => Promise<void>
  hovered: boolean
  selectedIds: Array<AssessmentStage | PracticeAssessmentStage>
  onHover: () => void
  onBlur: () => void
  isLoading: boolean
  readOnly?: boolean
}) => {
  const { selected, active } = useMemo(
    () => ({
      selected: selectedIds?.includes(id),
      active: stages?.includes(id),
    }),
    [stages, selectedIds, id],
  )
  const pointerEvents = useMemo(() => {
    if (readOnly) return 'none'
    if (mode && active) return 'none'
    if (mode) return 'auto'
    return active ? 'auto' : 'none'
  }, [active, mode, readOnly])

  return (
    <MenuItem
      zIndex={1}
      py={2}
      color="gray.600"
      pointerEvents={pointerEvents}
      onClick={onClick}
      closeOnSelect={false}
      fontWeight={600}
      background={hovered && (mode || active) ? 'gray.50' : 'white'}
      px={4}
      onPointerEnter={onHover}
      onPointerLeave={onBlur}>
      {id}
      <Center
        borderRadius={mode === 'move' ? 'full' : 4}
        ml="auto"
        bg={active && mode ? '#efefef' : 'transparent'}
        w={6}
        h={6}
        border={`1px solid ${mode ? '#cdcdcd' : 'transparent'}`}>
        {active || selected ? (
          <CheckIcon
            color={
              mode === 'move' && selectedIds.length && !selected ? 'gray.300' : colors.green.hex
            }
            display={hovered || isLoading ? 'none' : 'flex'}
            fontSize="0.8rem"
          />
        ) : null}
        {active || selected ? (
          <MinusIcon
            color="red.600"
            display={!hovered || isLoading ? 'none' : 'flex'}
            fontSize="0.8rem"
          />
        ) : null}
        {isLoading ? (
          <CircularProgress size="20px" color={colors.green.hex} isIndeterminate />
        ) : null}
      </Center>
    </MenuItem>
  )
}

const StatusEditHeader = <T extends AssessmentStage | PracticeAssessmentStage>({
  mode,
  loadingIds,
  handleCancel,
  handleConfirm,
  setMode,
  stageHistory,
  setMoveMode,
}: {
  mode: 'add' | 'move' | null
  loadingIds: Array<T>
  handleCancel: () => void
  handleConfirm: () => void
  setMode: (mode: 'add' | 'move' | null) => void
  stageHistory: Array<StageHistoryItem<T>> | undefined
  setMoveMode: () => void
}) => (
  <HStack w="100%" py={2} bg="gray.100" px={2} justify="space-between">
    {mode ? (
      <>
        <ActionButton
          isDisabled={!!loadingIds.length}
          onClick={handleCancel}
          bg="white"
          size="sm"
          variant="outline">
          Cancel
        </ActionButton>
        <SolidActionButton isLoading={!!loadingIds.length} onClick={handleConfirm} size="sm">
          Save
        </SolidActionButton>
      </>
    ) : (
      <>
        <SolidActionButton px={3} borderRadius="full" onClick={() => setMode('add')} size="sm">
          Add to List(s)
        </SolidActionButton>
        <PregnancyStatusHistoryButton stageHistory={stageHistory} />
        <SolidActionButton px={3} borderRadius="full" onClick={setMoveMode} size="sm">
          Move to List
        </SolidActionButton>
      </>
    )}
  </HStack>
)

export const PregnancyStatusDropdown = ({
  isMobile,
  practiceStatus,
  readOnly,
  ...props
}: MenuButtonProps & {
  isMobile?: boolean
  practiceStatus?: boolean
  readOnly?: boolean
}) => {
  const { selectedAssessment } = useProfile()
  const { populated } = selectedAssessment ?? {}
  const stages = useMemo(
    () => (practiceStatus ? populated?.practiceStages : populated?.adminStages),
    [populated, practiceStatus],
  )
  const stageHistory = useMemo(
    () => (practiceStatus ? populated?.practiceStageHistory : populated?.adminStageHistory),
    [populated, practiceStatus],
  )

  const disclosure = useDisclosure()

  const { isOpen, onClose, onOpen } = disclosure
  const {
    hovered,
    setHovered,
    mode,
    setMode,
    selectedIds,
    loadingIds,
    handleCancel,
    handleConfirm,
    handleItemClick,
    setMoveMode,
  } = usePregnancyStatus(disclosure)

  return (
    <Menu isOpen={isOpen} onClose={onClose} onOpen={onOpen} closeOnBlur={false} strategy="fixed">
      <MenuButton
        as={Button}
        variant="outline"
        color="blackAlpha.700"
        bg="white"
        // px={4}
        size={isMobile ? 'sm' : 'md'}
        borderRadius="full"
        // boxShadow='0 0 4px rgba(0,0,0.5)'
        shadow="md"
        {...props}>
        <Flex align="center" justify="center">
          <Text fontWeight={500} fontFamily="Hero-New" lineHeight={1}>
            {stages?.join(', ') || 'Not on any lists'}
          </Text>
        </Flex>
      </MenuButton>
      <Portal>
        <MenuList border="1px solid #cdcdcd" boxShadow="md" w="370px" py={0}>
          <VStack w="100%" divider={<Divider />} spacing={0}>
            {readOnly ? null : (
              <StatusEditHeader
                mode={mode}
                loadingIds={loadingIds}
                handleCancel={handleCancel}
                handleConfirm={handleConfirm}
                setMode={setMode}
                stageHistory={stageHistory}
                setMoveMode={setMoveMode}
              />
            )}
            {(practiceStatus ? practiceAssessmentStageKeys : assessmentStageKeys).map(k => (
              <AssessmentStatusButton
                key={k}
                id={k}
                onBlur={() => setHovered(null)}
                mode={mode}
                hovered={hovered === k}
                selectedIds={selectedIds}
                isLoading={loadingIds.includes(k)}
                onClick={() => handleItemClick(k)}
                onHover={() => setHovered(k)}
                readOnly={readOnly}
                stages={stages}
              />
            ))}
            <PregnancyArchiveStatus readOnly={readOnly} practiceStatus={practiceStatus} />
          </VStack>
        </MenuList>
      </Portal>
    </Menu>
  )
}

const PregnancyArchivedBadge = ({
  practiceStatus,
  isMobile,
}: {
  practiceStatus?: boolean
  isMobile?: boolean
}) => {
  const { selectedAssessment } = useProfile()
  const { practiceData, assessmentData } = selectedAssessment ?? {}
  const { appName } = useApp()

  const archivedOn = useMemo(
    () => (practiceStatus ? practiceData?.archivedOn : assessmentData?.archivedOn),
    [practiceStatus, assessmentData, practiceData],
  )
  const text = useMemo(() => {
    if (appName === 'app') return practiceStatus ? 'ARCHIVED ON PRACTICE' : 'ARCHIVED ON HB'
    return practiceStatus ? 'ARCHIVED' : 'ARCHIVED ON HB'
  }, [practiceStatus, appName])
  const body = archivedOn ? (
    <Tooltip
      bg="red.100"
      color="red.700"
      hasArrow
      placement="left"
      label={`Archived on ${getDateString(archivedOn, 'short')}`}>
      <Badge
        w={isMobile ? '100%' : 'auto'}
        colorScheme="red"
        fontSize="xs"
        fontFamily="Hero-New"
        textAlign="center">
        {text}
      </Badge>
    </Tooltip>
  ) : null
  return isMobile ? (
    <Flex flex={1} align="center">
      {body}
    </Flex>
  ) : (
    body
  )
}

const PregnancyArchivedContainer = ({
  children,
  isMobile,
}: {
  children: React.ReactNode
  isMobile?: boolean
}) => {
  const { appName } = useApp()

  return (
    <Flex
      flexFlow={isMobile && appName === 'app' ? 'row' : 'column'}
      gap={1}
      align="flex-end"
      justify={isMobile ? 'center' : 'flex-start'}>
      {children}
    </Flex>
  )
}
const PregnancyArchivedBadges = ({ isMobile }: { isMobile?: boolean }) => {
  const { appName } = useApp()
  if (appName === 'providers-app') {
    return (
      <PregnancyArchivedContainer isMobile={isMobile}>
        <PregnancyArchivedBadge isMobile={isMobile} practiceStatus />
        <PregnancyArchivedBadge isMobile={isMobile} />
      </PregnancyArchivedContainer>
    )
  }
  return (
    <PregnancyArchivedContainer isMobile={isMobile}>
      <PregnancyArchivedBadge isMobile={isMobile} />
      <PregnancyArchivedBadge isMobile={isMobile} practiceStatus />
    </PregnancyArchivedContainer>
  )
}

export const AdminPregnancyStatusDropdowns = ({ isMobile }: { isMobile?: boolean }) => (
  <Flex
    w={isMobile ? '100%' : 'auto'}
    justify={isMobile ? 'space-between' : 'flex-start'}
    py={isMobile ? 1 : 0}
    gap={2}
    align="center">
    <PregnancyArchivedBadges isMobile={isMobile} />
    <PregnancyStatusDropdown />
  </Flex>
)

const PracticePregnancyStatusDropdowns = ({ isMobile }: { isMobile?: boolean }) => (
  <Flex gap={2} align="center" justify="flex-end">
    <PregnancyArchivedBadges isMobile={isMobile} />
    <PregnancyStatusDropdown practiceStatus isMobile={isMobile} />
  </Flex>
)

export const PregnancyStatusDropdowns = ({ isMobile }: { isMobile?: boolean }) => {
  const { appName } = useApp()
  if (appName === 'app') {
    return <AdminPregnancyStatusDropdowns isMobile={isMobile} />
  }
  return <PracticePregnancyStatusDropdowns isMobile={isMobile} />
}
